import { createContext, useContext, useEffect, useState } from 'react'
import { getCssVar } from '@/utils/css-var'

type Theme = 'dark' | 'light' | 'system'

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
  railLabels: boolean
  setRailLabels: (value: boolean) => void
}

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
  railLabels: true,
  setRailLabels: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

const RAIL_LABELS_KEY = 'vite-ui-rail-labels'

/**
 * Tints the browser bar and the home-screen app's status bar (the theme-color meta of
 * index.html) with the page background of the theme now applied.
 */
function syncThemeColor() {
  const background = getCssVar('background')
  if (background) {
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', background)
  }
}

export function ThemeProvider({
  children,
  defaultTheme = 'dark',
  storageKey = 'vite-ui-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  )
  const [railLabels, setRailLabels] = useState<boolean>(() => {
    const stored = localStorage.getItem(RAIL_LABELS_KEY)
    return stored === null ? true : stored === 'true'
  })

  useEffect(() => {
    const root = window.document.documentElement

    root.classList.remove('light', 'dark')

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'

      root.classList.add(systemTheme)
    } else {
      root.classList.add(theme)
    }

    syncThemeColor()
  }, [theme])

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme)
      setTheme(theme)
    },
    railLabels,
    setRailLabels: (value: boolean) => {
      localStorage.setItem(RAIL_LABELS_KEY, value ? 'true' : 'false')
      setRailLabels(value)
    },
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined) throw new Error('useTheme must be used within a ThemeProvider')

  return context
}
